import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Phone,
  Smartphone,
  Globe,
  MapPin,
  Building2,
  User,
  Pencil,
  Archive,
  ArchiveRestore,
  Users,
  Camera,
  Shield,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useUpdateContact,
  useArchiveContact,
  useRestoreContact,
  useContactTags,
  useContactsByOrganization,
  useOrganizationContacts,
  useUploadContactAvatar,
} from "@/hooks/useContacts";
import type { Contact, ContactTag } from "@/hooks/useContacts";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "cellar_manager", label: "Manager" },
  { value: "cellar_staff", label: "Staff" },
  { value: "client_viewer", label: "Client Viewer" },
];

interface ContactDetailSheetProps {
  contact: Contact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactUpdated?: (contact: Contact) => void;
}

export function ContactDetailSheet({
  contact: contactProp,
  open,
  onOpenChange,
  onContactUpdated,
}: ContactDetailSheetProps) {
  const { toast } = useToast();
  const [displayContact, setDisplayContact] = useState<Contact | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneExt, setPhoneExt] = useState("");
  const [phoneMobile, setPhoneMobile] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [visibleTo, setVisibleTo] = useState<string[]>([]);

  const updateContact = useUpdateContact();
  const archiveContact = useArchiveContact();
  const restoreContact = useRestoreContact();
  const uploadAvatar = useUploadContactAvatar();
  const { data: tags = [] } = useContactTags();
  const { data: orgContacts = [] } = useOrganizationContacts();

  // Linked people (when viewing an organization)
  const { data: linkedPeople = [] } = useContactsByOrganization(
    displayContact?.type === "organization" ? displayContact.id : null
  );

  // Sync from prop
  useEffect(() => {
    setDisplayContact(contactProp);
    setIsEditing(false);
  }, [contactProp]);

  const handleEdit = () => {
    if (!displayContact) return;
    setFirstName(displayContact.first_name || "");
    setLastName(displayContact.last_name || "");
    setCompany(displayContact.company || "");
    setRole(displayContact.role || "");
    setEmail(displayContact.email || "");
    setPhone(displayContact.phone || "");
    setPhoneExt(displayContact.phone_ext || "");
    setPhoneMobile(displayContact.phone_mobile || "");
    setWebsite(displayContact.website || "");
    setAddress(displayContact.address || "");
    setNotes(displayContact.notes || "");
    setSelectedTags(displayContact.tags || []);
    setOrganizationId(displayContact.organization_id || "");
    setVisibleTo(displayContact.visible_to || []);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!displayContact) return;
    updateContact.mutate(
      {
        id: displayContact.id,
        input: {
          first_name: firstName || null,
          last_name: lastName || null,
          company: company || null,
          role: role || null,
          email: email || null,
          phone: phone || null,
          phone_ext: phoneExt || null,
          phone_mobile: phoneMobile || null,
          website: website || null,
          address: address || null,
          notes: notes || null,
          tags: selectedTags,
          organization_id: organizationId || null,
          visible_to: visibleTo.length > 0 ? visibleTo : null,
        },
      },
      {
        onSuccess: (data) => {
          setDisplayContact(data);
          onContactUpdated?.(data);
          setIsEditing(false);
        },
      }
    );
  };

  const handleArchive = () => {
    if (!displayContact) return;
    archiveContact.mutate(displayContact.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleRestore = () => {
    if (!displayContact) return;
    restoreContact.mutate(displayContact.id, {
      onSuccess: (data) => {
        setDisplayContact(data);
        onContactUpdated?.(data);
      },
    });
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !displayContact) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Avatar must be less than 2MB.",
        variant: "destructive",
      });
      return;
    }

    uploadAvatar.mutate(
      { contactId: displayContact.id, file },
      {
        onSuccess: (data) => {
          setDisplayContact(data);
          onContactUpdated?.(data);
        },
      }
    );
  };

  const toggleTag = (slug: string) => {
    setSelectedTags((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const toggleVisibleTo = (roleValue: string) => {
    setVisibleTo((prev) =>
      prev.includes(roleValue)
        ? prev.filter((r) => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  const displayName =
    displayContact?.type === "organization"
      ? displayContact?.company || "Organization"
      : [displayContact?.first_name, displayContact?.last_name]
          .filter(Boolean)
          .join(" ") || "Contact";

  const avatarInitial =
    displayContact?.type === "organization"
      ? (displayContact?.company?.[0] || "O")
      : (displayContact?.first_name?.[0] || displayContact?.last_name?.[0] || "C");

  const tagMap = tags.reduce<Record<string, ContactTag>>((acc, t) => {
    acc[t.tag] = t;
    return acc;
  }, {});

  if (!displayContact) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-4 pr-4">
              {/* Avatar */}
              <div className="relative group flex-shrink-0">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={displayContact.avatar_url || undefined} />
                  <AvatarFallback className="text-lg">
                    {avatarInitial.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Camera className="h-5 w-5 text-white" />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-xl truncate">{displayName}</SheetTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="capitalize">
                    {displayContact.type}
                  </Badge>
                  {!displayContact.is_active && (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      Archived
                    </Badge>
                  )}
                  {displayContact.visible_to && displayContact.visible_to.length > 0 && (
                    <Badge variant="secondary" className="gap-1">
                      <Shield className="h-3 w-3" />
                      Restricted
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* ── View Mode ─────────────────────────────── */}
            {!isEditing ? (
              <>
                {/* Tags */}
                {displayContact.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {displayContact.tags.map((slug) => {
                      const tag = tagMap[slug];
                      return (
                        <Badge
                          key={slug}
                          variant="secondary"
                          style={
                            tag?.color
                              ? { backgroundColor: tag.color, color: "#fff" }
                              : undefined
                          }
                        >
                          {tag?.label || slug}
                        </Badge>
                      );
                    })}
                  </div>
                )}

                {/* Contact info */}
                <div className="space-y-3">
                  {displayContact.type === "person" && displayContact.company && (
                    <InfoRow icon={Building2} label="Company" value={displayContact.company} />
                  )}
                  {displayContact.role && (
                    <InfoRow icon={User} label={displayContact.type === "person" ? "Title" : "Category"} value={displayContact.role} />
                  )}
                  {displayContact.email && (
                    <InfoRow icon={Mail} label="Email" value={displayContact.email} href={`mailto:${displayContact.email}`} />
                  )}
                  {displayContact.phone && (
                    <InfoRow
                      icon={Phone}
                      label="Phone"
                      value={
                        displayContact.phone +
                        (displayContact.phone_ext ? ` x${displayContact.phone_ext}` : "")
                      }
                      href={`tel:${displayContact.phone}`}
                    />
                  )}
                  {displayContact.phone_mobile && (
                    <InfoRow icon={Smartphone} label="Mobile" value={displayContact.phone_mobile} href={`tel:${displayContact.phone_mobile}`} />
                  )}
                  {displayContact.website && (
                    <InfoRow icon={Globe} label="Website" value={displayContact.website} href={displayContact.website} />
                  )}
                  {displayContact.address && (
                    <InfoRow icon={MapPin} label="Address" value={displayContact.address} />
                  )}
                </div>

                {/* Visibility */}
                {displayContact.visible_to && displayContact.visible_to.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Visible To</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {displayContact.visible_to.map((r) => {
                          const roleOption = ROLE_OPTIONS.find((o) => o.value === r);
                          return (
                            <Badge key={r} variant="outline">
                              {roleOption?.label || r}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {/* Notes */}
                {displayContact.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-1">Notes</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {displayContact.notes}
                      </p>
                    </div>
                  </>
                )}

                {/* Linked people (org view) */}
                {displayContact.type === "organization" && linkedPeople.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-medium">
                          People ({linkedPeople.length})
                        </p>
                      </div>
                      <div className="space-y-2">
                        {linkedPeople.map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {[p.first_name, p.last_name].filter(Boolean).join(" ")}
                              </p>
                              {p.role && (
                                <p className="text-xs text-muted-foreground">{p.role}</p>
                              )}
                            </div>
                            {p.email && (
                              <a
                                href={`mailto:${p.email}`}
                                className="text-xs text-primary hover:underline"
                              >
                                {p.email}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Actions */}
                <Separator />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {displayContact.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowArchiveDialog(true)}
                    >
                      <Archive className="h-4 w-4 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleRestore}>
                      <ArchiveRestore className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* ── Edit Mode ─────────────────────────────── */
              <div className="space-y-4">
                {displayContact.type === "person" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{displayContact.type === "organization" ? "Organization Name" : "Company"}</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} />
                </div>

                {displayContact.type === "person" && orgContacts.length > 0 && (
                  <div className="space-y-2">
                    <Label>Organization</Label>
                    <Select
                      value={organizationId || "__none__"}
                      onValueChange={(v) => setOrganizationId(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Link to an organization..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {orgContacts.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{displayContact.type === "person" ? "Title / Role" : "Category"}</Label>
                  <Input value={role} onChange={(e) => setRole(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Phone</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ext</Label>
                    <Input value={phoneExt} onChange={(e) => setPhoneExt(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input value={phoneMobile} onChange={(e) => setPhoneMobile(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input value={website} onChange={(e) => setWebsite(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
                </div>

                {/* Tags */}
                {tags.length > 0 && (
                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag: ContactTag) => {
                        const isSelected = selectedTags.includes(tag.tag);
                        return (
                          <Badge
                            key={tag.id}
                            variant={isSelected ? "default" : "outline"}
                            className="cursor-pointer select-none"
                            style={
                              isSelected && tag.color
                                ? { backgroundColor: tag.color, borderColor: tag.color, color: "#fff" }
                                : undefined
                            }
                            onClick={() => toggleTag(tag.tag)}
                          >
                            {tag.label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Visible To */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" />
                    Visible To
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Leave empty for all users. Select roles to restrict visibility.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ROLE_OPTIONS.map((opt) => {
                      const isSelected = visibleTo.includes(opt.value);
                      return (
                        <Badge
                          key={opt.value}
                          variant={isSelected ? "default" : "outline"}
                          className="cursor-pointer select-none"
                          onClick={() => toggleVisibleTo(opt.value)}
                        >
                          {opt.label}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={updateContact.isPending}>
                    {updateContact.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Archive confirmation */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {displayName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This contact will be hidden from active views. You can restore it
              later from the archived contacts list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function InfoRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {href ? (
          <a
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
            className="text-sm text-primary hover:underline break-all"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm break-all">{value}</p>
        )}
      </div>
    </div>
  );
}
